import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Loader2 } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { CtaBand } from "../../components/public/Shared";
import { api, mediaUrl } from "../../lib/api";

function Compare({ before, after }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const move = useCallback((clientX) => {
    const el = wrapRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  }, []);
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => move(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove); window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp); window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("touchmove", onMove); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchend", onUp); };
  }, [dragging, move]);

  return (
    <div ref={wrapRef} data-testid="project-before-after" className="relative w-full aspect-[16/10] rounded-3xl overflow-hidden select-none bg-[#1A2421]">
      <img src={mediaUrl(before)} alt="Before" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={mediaUrl(after)} alt="After" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <span className="absolute top-4 left-4 z-20 text-[11px] font-bold tracking-widest text-white/90 bg-black/40 backdrop-blur px-3 py-1 rounded-full">BEFORE</span>
      <span className="absolute top-4 right-4 z-20 text-[11px] font-bold tracking-widest text-[#0c1a12] bg-[#62B848] px-3 py-1 rounded-full">AFTER</span>
      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[3px] h-full bg-[#62B848] shadow-[0_0_20px_rgba(98,184,72,0.7)]" />
        <button onMouseDown={(e) => { e.preventDefault(); setDragging(true); }} onTouchStart={() => setDragging(true)}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full bg-[#62B848] text-[#0c1a12] shadow-xl cursor-ew-resize" aria-label="Drag to compare">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 6l-4 6 4 6M15 6l4 6-4 6" /></svg>
        </button>
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    setP(undefined);
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug]);

  if (p === undefined) return <div className="min-h-[70vh] grid place-items-center bg-[#F8F9FA]"><Loader2 size={30} className="animate-spin text-[#62B848]" /></div>;

  if (p === null) return (
    <div className="min-h-[70vh] grid place-items-center bg-[#F8F9FA] px-5">
      <div className="text-center">
        <h1 className="font-display font-extrabold text-3xl text-[#1A2421] mb-3">Project not found</h1>
        <p className="text-[#4A5550] mb-6">It may have been unpublished. Browse the rest of our work.</p>
        <Link to="/our-work" className="inline-flex items-center gap-2 bg-[#224733] text-white font-bold px-6 py-3 rounded-full">Back to Our Work</Link>
      </div>
    </div>
  );

  const cover = p.photos?.[0] || p.after_url || p.before_url;
  const hasBA = p.before_url && p.after_url;
  const galleryPhotos = p.photos || [];
  const projJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: p.title,
    description: p.summary || p.description,
    ...(cover ? { image: mediaUrl(cover) } : {}),
    ...(p.suburb ? { locationCreated: { "@type": "Place", name: `${p.suburb}, Perth WA` } } : {}),
    creator: { "@type": "RoofingContractor", name: "West Face Roofing" },
  };

  return (
    <>
      <Seo title={`${p.title} — Our Work`} description={p.summary || p.description || `${p.project_type} project in ${p.suburb || "Perth"} by West Face Roofing.`} path={`/our-work/${p.slug}`} />
      <Helmet>
        {cover && <meta property="og:image" content={mediaUrl(cover)} />}
        <script type="application/ld+json">{JSON.stringify(projJsonLd)}</script>
      </Helmet>

      <section className="pt-32 md:pt-40 pb-10 bg-[#224733] text-white relative overflow-hidden wf-grain">
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-10">
          <Link to="/our-work" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold mb-6"><ArrowLeft size={16} /> All work</Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {p.status === "in_progress"
              ? <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0c1a12] bg-[#62B848] px-3 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-[#0c1a12] animate-pulse" /> In Progress</span>
              : <span className="text-[11px] font-bold text-white bg-white/15 px-3 py-1 rounded-full">Completed</span>}
            {p.client_type && <span className="text-[11px] font-bold text-white/90 bg-white/10 px-3 py-1 rounded-full">{p.client_type}</span>}
            {p.project_type && <span className="text-[11px] font-bold text-white/90 bg-white/10 px-3 py-1 rounded-full">{p.project_type}</span>}
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none max-w-3xl">{p.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-white/70 text-sm">
            {p.suburb && <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-[#62B848]" /> {p.suburb}, Perth WA</span>}
            {p.date && <span>{new Date(p.date).toLocaleDateString("en-AU", { month: "long", year: "numeric" })}</span>}
          </div>
          {p.summary && <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">{p.summary}</p>}
        </div>
      </section>

      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 space-y-10">
          {hasBA && (
            <div>
              <h2 className="font-display font-extrabold text-2xl text-[#1A2421] mb-4">Before &amp; after</h2>
              <Compare before={p.before_url} after={p.after_url} />
            </div>
          )}

          {p.description && (
            <div className="bg-white rounded-3xl border border-black/5 p-6 md:p-9">
              <h2 className="font-display font-extrabold text-2xl text-[#1A2421] mb-3">About this job</h2>
              <p className="text-[#4A5550] text-lg leading-relaxed whitespace-pre-wrap">{p.description}</p>
            </div>
          )}

          {galleryPhotos.length > 0 && (
            <div>
              <h2 className="font-display font-extrabold text-2xl text-[#1A2421] mb-4">Gallery</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {galleryPhotos.map((url, i) => (
                  <motion.div key={url + i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 3) * 0.05 }}
                    className={`overflow-hidden rounded-3xl ${i === 0 ? "sm:col-span-2 lg:col-span-2 h-[280px] sm:h-[420px]" : "h-[280px]"}`}>
                    <img src={mediaUrl(url)} alt={`${p.title} — ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center pt-2">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold px-7 py-3.5 rounded-full transition-colors">Get a quote like this <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
